/**
 * React adapter (`cam2qr/react`): a hook that owns a QrScanner's lifecycle.
 * React is an optional peer dependency — this entry point is only bundled
 * when imported.
 */
import { useEffect, useRef, useState } from 'react';
import {
  QrScanner,
  type QrScannerOptions,
  type QrScannerUpdate,
  type ScannerError,
  type ScannerInternals,
} from './scanner/scanner.js';
import type { QrResult } from './types.js';

export interface UseQrScannerOptions extends QrScannerOptions {
  /** Set false to keep the camera off (and release it). Default true. */
  enabled?: boolean;
  /**
   * Suspend/resume decoding while keeping the camera stream warm (no
   * black-flash or permission churn — distinct from `enabled`, which releases
   * the camera). Reactive: toggling it calls `pause()`/`resume()` on the live
   * scanner. `paused: true` from the first render starts suspended and lands
   * even during the async camera-startup window.
   */
  paused?: boolean;
}

export interface UseQrScannerReturn {
  /** Attach to the <video> element: `<video ref={videoRef} />`. */
  videoRef: (video: HTMLVideoElement | null) => void;
  /** Latest decoded result (state, re-renders on change). */
  result: QrResult | null;
  /** Latest camera/decode error, including start() failures. */
  error: ScannerError | null;
  /** True between the scanner's start and stop events. */
  isScanning: boolean;
  /** The underlying scanner for imperative control (torch, setCamera, …). */
  scanner: QrScanner | null;
}

/**
 * Starts scanning as soon as a video element is attached (and `enabled` is
 * not false); stops and releases the camera on unmount or when disabled.
 * Toggling `paused` suspends/resumes decoding without touching the camera.
 *
 * Options in the `QrScannerUpdate` set (`maxScansPerSecond`, `scanRegion`,
 * `tryInverted`, `tryHarder`, `maxDownscale`, `dedupeWindowMs`, `stopOnDecode`,
 * `multiple`, `structuredAppend`) are applied to the running scanner whenever
 * they change — pass them straight from state. The rest are fixed at scanner
 * creation because they cannot be changed without reacquiring the camera:
 * change `camera` with `scanner.setCamera()`, and treat `useWorker`,
 * `useNativeDetector` and `pauseOnHidden` as mount-time config.
 */
export function useQrScanner(
  options: UseQrScannerOptions = {},
  internals?: ScannerInternals,
): UseQrScannerReturn {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [result, setResult] = useState<QrResult | null>(null);
  const [error, setError] = useState<ScannerError | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanner, setScanner] = useState<QrScanner | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const enabled = options.enabled !== false;
  const paused = options.paused === true;

  useEffect(() => {
    if (!video || !enabled) return undefined;
    const instance = new QrScanner(
      video,
      {
        ...optionsRef.current,
        onDecode(decodeResult) {
          setResult(decodeResult);
          optionsRef.current.onDecode?.(decodeResult);
        },
        onError(scanError) {
          setError(scanError);
          optionsRef.current.onError?.(scanError);
        },
      },
      internals,
    );
    instance.on('start', () => setIsScanning(true));
    instance.on('stop', () => setIsScanning(false));
    setScanner(instance);
    instance.start().catch((startError: ScannerError) => setError(startError));
    // Honor an initial paused intent; QrScanner remembers a pause requested
    // during the async 'starting' window and applies it once scanning begins.
    if (optionsRef.current.paused === true) instance.pause();
    return () => {
      instance.destroy();
      setScanner(null);
      setIsScanning(false);
    };
  }, [video, enabled, internals]);

  // Reactive pause/resume on the live scanner — keeps the stream warm.
  useEffect(() => {
    if (!scanner) return;
    if (paused) scanner.pause();
    else scanner.resume();
  }, [scanner, paused]);

  // Push live-tunable options through on change. React re-evaluates the options
  // object every render, so a caller passing `dedupeWindowMs={fromState}` has
  // every reason to expect it to take effect; freezing it at scanner creation
  // is a silent no-op. These are exactly the options update() accepts — the
  // ones that need no camera reacquisition — and each is compared by value, so
  // an inline options literal does not thrash.
  const {
    maxScansPerSecond,
    scanRegion,
    tryInverted,
    tryHarder,
    maxDownscale,
    dedupeWindowMs,
    stopOnDecode,
    multiple,
    structuredAppend,
  } = options;
  useEffect(() => {
    if (!scanner) return;
    // Only defined values are forwarded: an omitted option means "leave it
    // alone", matching update()'s own semantics (and exactOptionalPropertyTypes
    // rules out passing an explicit undefined).
    const update: QrScannerUpdate = {};
    if (maxScansPerSecond !== undefined) update.maxScansPerSecond = maxScansPerSecond;
    if (scanRegion !== undefined) update.scanRegion = scanRegion;
    if (tryInverted !== undefined) update.tryInverted = tryInverted;
    if (tryHarder !== undefined) update.tryHarder = tryHarder;
    if (maxDownscale !== undefined) update.maxDownscale = maxDownscale;
    if (dedupeWindowMs !== undefined) update.dedupeWindowMs = dedupeWindowMs;
    if (stopOnDecode !== undefined) update.stopOnDecode = stopOnDecode;
    if (multiple !== undefined) update.multiple = multiple;
    if (structuredAppend !== undefined) update.structuredAppend = structuredAppend;
    scanner.update(update);
  }, [
    scanner,
    maxScansPerSecond,
    scanRegion,
    tryInverted,
    tryHarder,
    maxDownscale,
    dedupeWindowMs,
    stopOnDecode,
    multiple,
    structuredAppend,
  ]);

  return { videoRef: setVideo, result, error, isScanning, scanner };
}
